import { forwardRef } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { observer } from 'mobx-react';
import { TextField, Select } from '@ui-framework/index';

const BOOLEAN_OPTIONS = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

function mergeRefs(forwardedRef, controllerRef) {
  return el => {
    if (typeof controllerRef === 'function') controllerRef(el);
    else if (controllerRef) {
      const r = controllerRef;
      r.current = el;
    }
    if (typeof forwardedRef === 'function') forwardedRef(el);
    else if (forwardedRef) {
      const r = forwardedRef;
      r.current = el;
    }
  };
}

const InsightConfigValue = forwardRef(
  ({ customConfig = {}, field, placeholder, error, ...props }, ref) => {
    const { watch, control } = useFormContext();
    const configName = watch?.('name');
    const types = customConfig?.types ?? {};
    const dataType = configName ? types[configName] || 'string' : 'string';

    const commonProps = {
      ...props,
      ref,
      id: field?.name,
      name: field?.name,
      placeholder: placeholder || '',
      error: !!error,
      helperText: error?.message,
    };

    if (dataType === 'boolean') {
      return (
        <Controller
          name={field?.name}
          control={control}
          render={({ field: controllerField }) => {
            const v = controllerField.value;
            let selectValue = '';
            if (v === true || v === 'true') selectValue = 'true';
            else if (v === false || v === 'false') selectValue = 'false';
            return (
              <Select
                ref={mergeRefs(ref, controllerField.ref)}
                options={BOOLEAN_OPTIONS}
                {...commonProps}
                {...controllerField}
                value={selectValue}
                overwriteProps={{
                  onChange: e =>
                    controllerField.onChange(e.target?.value ?? ''),
                }}
              />
            );
          }}
        />
      );
    }

    if (dataType === 'number' || dataType === 'positiveNumber') {
      return (
        <Controller
          name={field?.name}
          control={control}
          render={({ field: controllerField }) => {
            const numVal =
              controllerField.value === '' || controllerField.value == null
                ? ''
                : Number(controllerField.value);
            return (
              <TextField
                ref={mergeRefs(ref, controllerField.ref)}
                {...commonProps}
                type="number"
                value={numVal}
                inputProps={{ min: 0, step: 1 }}
                onChange={e => {
                  const v = e.target?.value;
                  controllerField.onChange(v === '' ? '' : String(v));
                }}
                onBlur={controllerField.onBlur}
              />
            );
          }}
        />
      );
    }

    if (dataType === 'phone') {
      return (
        <TextField
          {...commonProps}
          {...props}
          type="tel"
          inputProps={{ inputMode: 'tel', autoComplete: 'tel' }}
        />
      );
    }

    if (dataType === 'url') {
      return (
        <TextField
          {...commonProps}
          {...props}
          type="url"
          inputProps={{ autoComplete: 'url' }}
        />
      );
    }

    if (
      dataType === 'commaSeparatedString' ||
      dataType === 'commaSeparatedPhoneNumber'
    ) {
      return (
        <TextField
          {...commonProps}
          {...props}
          type="text"
          inputProps={
            dataType === 'commaSeparatedPhoneNumber'
              ? { inputMode: 'tel' }
              : undefined
          }
          placeholder={
            placeholder ||
            (dataType === 'commaSeparatedPhoneNumber'
              ? 'Comma separated phone numbers'
              : 'Comma separated values')
          }
        />
      );
    }

    return <TextField {...commonProps} {...props} type="text" />;
  },
);

export default observer(InsightConfigValue);
